// Per-developer brand colours for the /developers index and /developers/<slug>
// detail pages — drives the rounded-square "dev mark" tile + card accents.
//
// Keyed by lowercased slug (matches `slugifyDeveloper(name)` in
// server/utils/availabilityBot/queries.ts) so we don't need to know the
// numeric postgres ID to look up a colour client-side. When the developer
// isn't in the map, `getDeveloperBrandColor(slug)` falls back to a
// deterministic hash-derived hue so every developer still gets a stable
// colour without us hand-curating the long tail.
//
// Colours pulled from the official brand identities where known; otherwise
// from the design pack (see /tmp/agents-design/Developers Index.html).

export const DEVELOPER_BRAND_COLORS: Record<string, string> = {
  'emaar': '#00693E',
  'emaar-properties': '#00693E',
  'damac': '#8B0000',
  'damac-properties': '#8B0000',
  'sobha': '#5B2A9C',
  'sobha-realty': '#5B2A9C',
  'meraas': '#EA6A2E',
  'nakheel': '#D44646',
  'aldar': '#3B82F6',
  'aldar-properties': '#3B82F6',
  'azizi': '#E85A2C',
  'azizi-developments': '#E85A2C',
  'select-group': '#244C7C',
  'ellington': '#1F8A5B',
  'ellington-properties': '#1F8A5B',
  'dubai-properties': '#7E5BC9',
  'omniyat': '#0D0F15',
  'binghatti': '#FFB648',
  'tiger': '#222831',
  'tiger-properties': '#222831',
  'prestige-one': '#4B3343',
  'samana': '#0E7C66',
  'samana-developers': '#0E7C66',
  'object-1': '#C84B5F',
  'danube': '#2A6FDB',
  'danube-properties': '#2A6FDB',
  'tabeer': '#8863B0',
  'tabeer-developments': '#8863B0',
  // Tier-2 / boutique additions can land here as we curate them.
  'arada': '#0E5E62',
  'meydan': '#0F5DA0',
  'mag': '#9A1B30',
  'mag-properties': '#9A1B30',
  'union-properties': '#0A3D62',
  'deyaar': '#A23B72',
  'reportage': '#274472',
  'iman': '#3F7D8C',
};

// Deterministic hash → HSL fallback. We pin saturation + lightness so all
// generated colours read as confident brand tones (not pastel, not neon).
// Hue is the only varying axis, derived from the slug bytes.
const fallbackHueFromSlug = (slug: string): number => {
  let h = 0;
  for (let i = 0; i < slug.length; i++) {
    h = (h << 5) - h + slug.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % 360;
};

export const getDeveloperBrandColor = (slug: string | null | undefined): string => {
  if (!slug) return '#1F2A44'; // neutral fallback when slug is missing
  const key = slug.toLowerCase();
  if (DEVELOPER_BRAND_COLORS[key]) return DEVELOPER_BRAND_COLORS[key];
  const hue = fallbackHueFromSlug(key);
  return `hsl(${hue}, 52%, 38%)`;
};

export const getDeveloperInitial = (name: string | null | undefined): string => {
  if (!name) return 'D';
  const trimmed = name.trim();
  if (!trimmed) return 'D';
  return trimmed.charAt(0).toUpperCase();
};
